import { usePortfolioChartColors } from '@/components/efriends/portfolioChartColors.js';
import { useHighlightNodes } from '@/composables/useHighlightNodes.ts';
import { useMapStore } from '@/stores/useMapStore';
import { useTourStore } from '@/stores/useTourStore';
import { usePortfolioStore } from '@/stores/usePortfolioStore';
import { computed, ref, watch, watchEffect } from 'vue';


export function useTour(router, map, google, mapInitialized) {
  const mapStore = useMapStore()
  const tourStore = useTourStore()
  const portfolioStore = usePortfolioStore()
  const { getPowerColor } = usePortfolioChartColors()

  const home = 'P01_3000171'
  const klara = 'P01_3000150'
  const julia = 'P01_3000740'

  // Show me and klara
  const visibleNodes = ref([home, klara])

  const step = ref(0)
  const showPortfolio = ref(false)
  const polylines = ref([])
  const homeNode = computed(() => mapStore.getCubeByName(home, 'tour'))

  const cubeList = computed(() =>
    mapStore.tourCubeList.filter((cube) =>
      visibleNodes.value.includes(cube.cubeName)
    )
  )

  const initTour = () => {
    tourStore.loadTourCubeList()

    // We initialize (=reset) the portfolio session in the tour to get rid of any
    // connections the user made in previous tours.
    portfolioStore.initPortfolioSession()

    const { highlightNodes } = useHighlightNodes()

    // Set connection to Klara (local state only — HTTP call happens at step 1)
    portfolioStore.cubes = [...new Set([...portfolioStore.cubes, klara])]

    watchEffect(() => {
      const current = portfolioStore.cubes.slice()

      if (mapInitialized.value && portfolioStore.cubes.length > 0) {
        visualizeConnections()
      }
    })

    watchEffect(() => {
      const current = portfolioStore.highlightedNodes.slice()

      if (
          !!map.value &&
          !!google.value &&
          portfolioStore.highlightedNodes.length > 0
      ) {
        highlightNodes(map.value, google.value, portfolioStore.highlightedNodes)
      }
    })
  }

  const isTourComplete = computed(() => tourStore.isTourComplete)

  function setStep(value) {
    step.value = value
  }

  function focusNode(cubeId) {
    const cube = mapStore.getCubeByName(cubeId, 'tour')
    map.value.panTo(
      new google.value.maps.LatLng(cube.params.latitude, cube.params.longitude)
    )
  }

  watch(step, (newStep) => {
    if (newStep === 1) {
      // Highlight Klara
      portfolioStore.highlightedNodes = [klara]

      // Push connection to Klara to backend
      portfolioStore.addCubeToPortfolio(klara)
      showPortfolio.value = true
    }
    if (newStep === 2) {
      // Highlight Julia
      visibleNodes.value = [...visibleNodes.value, julia]
      portfolioStore.highlightedNodes = [julia]
    }
    if (newStep === 3) {
      // Ensure connection to Julia is made
      portfolioStore.addCubeToPortfolio(julia)
      // Close Popup
      router.replace({ params: { selected: null } })
      // Focus goes back to stvalue
      focusNode(home)
      portfolioStore.highlightedNodes = []
    }
  })

  // If user connects to go to next step
  const portfolioCubes = computed(() => portfolioStore.cubes)
  watch(portfolioCubes, () => {
    if (portfolioCubes.value.includes(julia)) {
      if (step.value === 2) { step.value++ }
    }
  })

  const visualizeConnections = () => {
    // Remove existing lines
    for (const line of polylines.value) {
      line.setMap(null)
    }

    const lineSymbol = {
      path: google.value.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      strokeWeight: 4,
    }

    const lineOptions = {
      geodesic: true,
      strokeOpacity: 1.0,
      strokeWeight: 3,
      icons: [
        {
          icon: lineSymbol,
          offset: '50px',
          repeat: '50px',
        },
      ],
    }

    for (let connection of portfolioStore.cubes) {
      const producer = mapStore.getCubeByName(connection, 'tour')
      const coordinates = [
        new google.value.maps.LatLng(
          producer.params.latitude,
          producer.params.longitude
        ),
        new google.value.maps.LatLng(
          homeNode.value.params.latitude,
          homeNode.value.params.longitude
        ),
      ]

      const path = new google.value.maps.Polyline({
        ...lineOptions,
        path: coordinates,
        strokeColor: getPowerColor(producer.cubeName),
      })

      path.setMap(map.value)
      polylines.value.push(path)

      animateLine(0, path)
    }
  }

  function animateLine(timestamp, path) {
    const icons = path.get('icons')
    const modulo = 1500
    const count = timestamp % modulo

    icons[0].offset = (count / modulo) * 50 + 'px'
    path.set('icons', icons)

    window.requestAnimationFrame((timestamp) => animateLine(timestamp, path))
  }


  return {
    isTourComplete,
    step,
    setStep,
    cubeList,
    initTour,
    showPortfolio,
  }
}
